/**
 * Settings → Messaging → Email your crew (slice 2h §8.1; Paper 70, 70b, 76):
 * the crew's address with Copy (and Change, the owner's), and the person's
 * own addresses, each Linked or waiting on its code, with Add address (up to
 * 5). Any person sees the crew's address; only they see their addresses.
 */
import { Button, buttonVariants } from "@crew/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@crew/ui/components/dropdown-menu";
import { Spinner } from "@crew/ui/components/spinner";
import { cn } from "@crew/ui/lib/utils";
import { errorHint } from "../../app/api";
import { Copy, Ellipsis, Plus } from "lucide-react";
import { useState, type ReactNode } from "react";
import type { Me } from "../../app/me";
import { StateLabel } from "../settings/integrations-parts";
import {
  useCreateEmailLink,
  useHeldCode,
  useRemoveLink,
  type EmailLink,
  type EmailView,
} from "./api";
import { ChangeAddress } from "./change-address";
import { useServerSeconds } from "./clock";
import { EmailWindow, type EmailWindowOpen } from "./email-window";
import {
  addressState,
  addressStatus,
  canAdd,
  cardState,
  footLine,
  keptLine,
  type AddressState,
} from "./email-view";
import { EmailTile } from "./parts";
import { useCopy } from "./window-kit";
import { ConfirmRemove } from "./window-parts";

/** A card's small button (Paper 70): 13px, 32px tall, outlined on the card. */
const SMALL = "h-8 gap-1.5 bg-card px-3 text-meta";

export function EmailSection({
  me,
  email,
  mobile,
}: {
  me: Me;
  email: EmailView | undefined;
  mobile: boolean;
}) {
  const [win, setWin] = useState<EmailWindowOpen | null>(null);
  const [changing, setChanging] = useState(false);
  const state = cardState(email);
  const pending = Boolean(email?.links.some((l) => l.status !== "linked"));
  const now = useServerSeconds(pending);
  return (
    <section aria-label="Email your crew" className="flex flex-col gap-2.5 md:gap-3" data-email>
      <div className="flex flex-col gap-0.5 md:flex-row md:items-baseline md:gap-2.5">
        <h2 className="text-question leading-6 font-semibold text-foreground">Email your crew</h2>
        <p className="text-meta leading-4.5 text-muted-foreground">
          {mobile
            ? "Email it, or forward it mail. It answers by email."
            : "Email your Chief of Staff, or forward it mail. It answers from your crew's address."}
        </p>
      </div>
      {state === "unavailable" || state === "none" || !email ? (
        <Unavailable />
      ) : (
        <div
          data-email-card={state}
          className="flex flex-col rounded-panel border border-border bg-card"
        >
          <AddressRow
            email={email}
            ready={state === "ready"}
            owner={me.user.role === "owner"}
            mobile={mobile}
            timezone={me.workspace.timezone}
            onChange={() => setChanging(true)}
          />
          <Addresses email={email} now={now} mobile={mobile} onWindow={setWin} />
        </div>
      )}
      {email?.address ? (
        <>
          <EmailWindow
            open={win}
            email={email}
            mobile={mobile}
            onClose={() => setWin(null)}
            onWindow={setWin}
          />
          <ChangeAddress
            open={changing}
            email={email}
            timezone={me.workspace.timezone}
            mobile={mobile}
            onClose={() => setChanging(false)}
          />
        </>
      ) : null}
    </section>
  );
}

/** Email isn't available (Paper 76): a real computer with no routing token, or no address yet. */
function Unavailable() {
  return (
    <div
      data-email-card="unavailable"
      className="flex items-center gap-4 rounded-panel border border-border bg-card px-5 py-4"
    >
      <EmailTile muted />
      <div className="flex flex-col gap-0.5">
        <span className="text-body leading-5 font-semibold text-foreground">Email</span>
        <span className="text-meta leading-4.5 text-muted-foreground">
          Email isn't available right now.
        </span>
      </div>
    </div>
  );
}

/** The crew's address, large, with Copy and the owner's Change (Paper 70, 70b, 76). */
/** The crew's address, large (a phone's breaks after its @), or that it's setting up. */
function AddressText({ full, ready }: { full: string; ready: boolean }) {
  if (!ready) {
    return (
      <span className="flex items-center gap-2 text-body leading-5 text-copy">
        <Spinner className="size-3.5" />
        Setting up {full}…
      </span>
    );
  }
  const [name, domain] = full.split("@");
  return (
    <span
      data-email-address={full}
      className="font-mono text-[17px] leading-6 font-medium tracking-tight wrap-anywhere text-foreground md:truncate md:text-[20px] md:leading-6.5"
    >
      {name}@
      <wbr />
      {domain}
    </span>
  );
}

/** Copy and Change beside the address on a desktop; on a phone, Email your crew and Copy address under it. */
function AddressButtons({
  full,
  change,
  mobile,
}: {
  full: string;
  change: ReactNode;
  mobile: boolean;
}) {
  const [copied, copy] = useCopy();
  if (mobile) {
    return (
      <div className="flex gap-2 [&>*]:h-auto [&>*]:flex-1 [&>*]:py-2.5 [&>*]:text-sm">
        <a href={`mailto:${full}`} className={buttonVariants({ size: "lg" })}>
          Email your crew
        </a>
        <Button variant="outline" size="lg" className="bg-card" onClick={() => copy(full)}>
          {copied ? "Copied" : "Copy address"}
        </Button>
      </div>
    );
  }
  return (
    <div className="flex shrink-0 items-center gap-2">
      <Button variant="outline" className={SMALL} onClick={() => copy(full)}>
        <Copy aria-hidden className="size-3.5" />
        {copied ? "Copied" : "Copy"}
      </Button>
      {change}
    </div>
  );
}

/** Under the address: a Change still setting up (Devin on #391), else the old address kept. */
function AddressNote({ email, timezone }: { email: EmailView; timezone: string }) {
  if (email.changing) {
    return (
      <span
        data-email-changing
        className="flex items-center gap-1.5 text-meta leading-4.5 text-copper"
      >
        <Spinner className="size-3" />
        Changing to {email.changing.full}…
      </span>
    );
  }
  const kept = keptLine(email, timezone);
  if (!kept) return null;
  return (
    <span data-email-kept className="text-meta leading-4.5 text-muted-foreground">
      Mail to {kept.full} still arrives until {kept.until}.
    </span>
  );
}

function AddressRow({
  email,
  ready,
  owner,
  mobile,
  timezone,
  onChange,
}: {
  email: EmailView;
  timezone: string;
  ready: boolean;
  owner: boolean;
  mobile: boolean;
  onChange: () => void;
}) {
  const full = email.address!.full;
  const change =
    owner && ready && !email.changing ? (
      <Button
        variant="ghost"
        className="h-8 px-2.5 text-meta text-muted-foreground"
        onClick={onChange}
      >
        Change
      </Button>
    ) : null;
  const buttons = ready ? <AddressButtons full={full} change={change} mobile={mobile} /> : null;
  return (
    <div className="flex flex-col gap-3.5 px-4 pt-4 pb-4 md:px-5 md:pt-5 md:pb-4.5">
      <div className="flex items-center gap-3.5 md:gap-4">
        <EmailTile />
        <div className="flex min-w-0 flex-1 basis-0 flex-col gap-0.5 md:gap-1">
          <span className="text-meta leading-4.5 text-muted-foreground">Your crew's address</span>
          <AddressText full={full} ready={ready} />
          <AddressNote email={email} timezone={timezone} />
        </div>
        {mobile ? change && <div className="self-start">{change}</div> : buttons}
      </div>
      {mobile ? buttons : null}
    </div>
  );
}

/** Your addresses: the head with Add address, a row each, and the foot (Paper 70, 70b, 76). */
function Addresses({
  email,
  now,
  mobile,
  onWindow,
}: {
  email: EmailView;
  now: number;
  mobile: boolean;
  onWindow: (open: EmailWindowOpen) => void;
}) {
  const add = canAdd(email) ? (
    <Button
      variant="outline"
      size={mobile ? "lg" : "default"}
      className={mobile ? "h-auto w-full gap-1.5 bg-card py-2.5 text-sm" : SMALL}
      onClick={() => onWindow({ linkId: null })}
    >
      <Plus aria-hidden className="size-3.5" />
      Add address
    </Button>
  ) : null;
  return (
    <div className="flex flex-col border-t border-border" data-email-addresses>
      <div className="flex items-center gap-4 px-4 pt-3.5 pb-2.5 md:pr-5 md:pl-17">
        <div className="flex flex-1 basis-0 flex-col gap-0.5">
          <span className="text-body leading-5 font-semibold text-foreground">Your addresses</span>
          <span className="text-meta leading-4.5 text-muted-foreground">
            Mail from these reaches your crew as you. Anything else is ignored.
            <span className="hidden md:inline"> Only you see them.</span>
          </span>
        </div>
        {mobile ? null : add}
      </div>
      {email.links.map((link) => (
        <AddressItem
          key={link.id}
          link={link}
          state={addressState(link, now)}
          mobile={mobile}
          onWindow={onWindow}
        />
      ))}
      <div
        className={cn(
          "flex flex-col gap-3 px-4 pt-3 pb-3.5 md:border-t md:border-border md:pr-5 md:pl-17",
          mobile && "items-stretch",
        )}
      >
        {mobile ? add : null}
        <span
          data-email-foot
          className={cn("text-meta leading-4.5 text-muted-foreground", mobile && "text-center")}
        >
          {footLine(email, mobile)}
        </span>
      </div>
    </div>
  );
}

/** An address (Paper 70, 76): its status lane, and Show code or New code while a code is out. */
/** The address and, on a desktop, Your login; on a phone its status under it (Paper 70, 70b). */
function AddressName({
  link,
  status,
  mobile,
}: {
  link: EmailLink;
  status: ReturnType<typeof addressStatus>;
  mobile: boolean;
}) {
  return (
    <div className="flex min-w-0 flex-1 basis-0 flex-col gap-0.5 md:flex-row md:items-center md:gap-2.5">
      <span className="truncate text-sm leading-5 text-foreground">{link.address}</span>
      {mobile ? (
        <StateLabel tone={status.tone} className="font-normal text-muted-foreground">
          {link.auto ? `${status.text} · Your login` : status.text}
        </StateLabel>
      ) : link.auto ? (
        <span className="shrink-0 rounded-tag bg-muted px-1.75 py-0.5 text-xs font-medium text-muted-foreground">
          Your login
        </span>
      ) : null}
    </div>
  );
}

/** A code out's action (Paper 76): Show code while this tab holds it, else New code. */
function CodeAction({
  link,
  state,
  creating,
  onShow,
  onNewCode,
}: {
  link: EmailLink;
  state: AddressState;
  creating: boolean;
  onShow: () => void;
  onNewCode: () => void;
}) {
  const held = useHeldCode(link.status === "linked" ? null : link.id);
  if (state.kind === "linked") return null;
  if (state.kind === "pending" && held) {
    return (
      <Button variant="outline" className={SMALL} onClick={onShow}>
        Show code
      </Button>
    );
  }
  return (
    <Button variant="outline" className={SMALL} disabled={creating} onClick={onNewCode}>
      New code
    </Button>
  );
}

/** An address (Paper 70, 76): its status lane, and Show code or New code while a code is out. */
function AddressItem({
  link,
  state,
  mobile,
  onWindow,
}: {
  link: EmailLink;
  state: AddressState;
  mobile: boolean;
  onWindow: (open: EmailWindowOpen) => void;
}) {
  const create = useCreateEmailLink();
  const status = addressStatus(state);
  const newCode = () =>
    create.mutate(link.address, { onSuccess: ({ link: made }) => onWindow({ linkId: made.id }) });
  return (
    <div className="flex flex-col border-t border-border">
      <div
        data-email-link={link.address}
        data-state={state.kind}
        className="flex items-center gap-4 px-4 py-2.5 md:pr-5 md:pl-17"
      >
        <AddressName link={link} status={status} mobile={mobile} />
        {mobile ? null : (
          <StateLabel tone={status.tone} className="w-42.5 shrink-0">
            {status.text}
          </StateLabel>
        )}
        {/* A lane of its own, so every row's status lines up (Paper 76). */}
        <div className="flex shrink-0 justify-end md:w-24">
          <CodeAction
            link={link}
            state={state}
            creating={create.isPending}
            onShow={() => onWindow({ linkId: link.id })}
            onNewCode={newCode}
          />
        </div>
        <AddressMenu link={link} />
      </div>
      {/* A New code that didn't go through says why (Devin on #391). */}
      {create.error ? (
        <p role="alert" className="px-4 pb-2.5 text-meta text-destructive md:pr-5 md:pl-17">
          {errorHint(create.error)}
        </p>
      ) : null}
    </div>
  );
}

/** Remove, which asks first (Paper 76); for a code out, Cancel. */
function AddressMenu({ link }: { link: EmailLink }) {
  const [asking, setAsking] = useState(false);
  const remove = useRemoveLink();
  const linked = link.status === "linked";
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              variant="ghost"
              size="icon"
              className="size-8 shrink-0 text-muted-foreground"
              aria-label={`${link.address}: more`}
            />
          }
        >
          <Ellipsis className="size-4" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-50 p-1.5">
          {linked ? (
            <DropdownMenuItem variant="destructive" onClick={() => setAsking(true)}>
              Remove
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem onClick={() => remove.mutate(link.id)}>
              Cancel the code
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <ConfirmRemove
        linkId={link.id}
        open={asking}
        onOpenChange={setAsking}
        title={`Remove ${link.address}?`}
        description="Mail from it stops reaching your crew. The chats it started stay in Chats. You can add it again with a new code."
        keep="Cancel"
        action="Remove"
        marker="data-remove-confirm"
      />
    </>
  );
}
